import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import { API_CONFIG } from '../../config';

/**
 * Public (unauthenticated) "Redeem your invite" page for the preview growth loop
 * (ADR-0053, #315). The redeemer arrives via a shared link carrying a ?t=<token>
 * peer-invite token, self-selects their email, and POSTs to the unauthenticated
 * developer-API route POST /developer/redeem-invite. On success their email is on
 * the allowlist and they're directed to create their account. The token + cap
 * ledger are the authorization (verified server-side); there is no PHI here.
 */
export default function RedeemInvitePage() {
  const [params] = useSearchParams();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [redeemed, setRedeemed] = useState(false);
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  // The token rides the link as ?t=… — capture it once on mount.
  useEffect(() => {
    setToken(params.get('t') || '');
  }, [params]);

  const onSubmit = async ({ email }) => {
    setError('');
    if (!token) {
      setError('This invite link is missing its code. Please use the full link you were sent.');
      return;
    }
    try {
      const resp = await fetch(`${API_CONFIG.developerApiBase}/developer/redeem-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.message || 'This invite link is invalid or has expired.');
      }
      setRedeemed(true);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  if (!token) {
    return (
      <AuthCard title="Invite link incomplete" subtitle="Vectros is in invite-only preview" align="center">
        <Typography variant="body2" color="text.secondary" textAlign="center">
          This page needs the full invite link you were sent (it carries a one-time code).
          Please open the original link again.
        </Typography>
        <Typography variant="body2" textAlign="center" mt={3} color="text.secondary">
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#3B82F6', fontWeight: 500 }}>Sign in</Link>
        </Typography>
      </AuthCard>
    );
  }

  if (redeemed) {
    return (
      <AuthCard title="You're in" subtitle="Vectros is in invite-only preview" align="center">
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Your access is confirmed. Create your account with this email to get started.
        </Typography>
        <Button component={Link} to="/register" variant="contained" fullWidth
          sx={{ mt: 3, bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          Create your account
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Redeem your invite" subtitle="You've been invited to the Vectros preview">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Your email" type="email" size="small" fullWidth
          helperText="This is the email you'll use to sign in."
          {...register('email', { required: true })} />
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          {isSubmitting ? 'Redeeming…' : 'Redeem invite'}
        </Button>
      </Box>
      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        Already have an account?{' '}
        <Link to="/login" style={{ color: '#3B82F6', fontWeight: 500 }}>Sign in</Link>
      </Typography>
    </AuthCard>
  );
}
